import moment from 'moment-timezone';

import {useTransformQuery} from 'web-app/react/hooks/use-transform-query';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import {BillingFrequency, Timeframe, BillingState, View} from 'web-app/util/graphql/api-types';
import {needsTranslations} from 'web-app/util/scoped-needs-translation';
import {pipe} from 'web-app/util/fp';
import {isEmpty} from 'web-app/util/array';

import type {
    PlanOverviewQuery,
    PlanOverviewQueryVariables,
} from '../queries/__generated__/plan-overview.query.api-types';
import {PlanOverview} from '../queries/plan-overview.query';
import type * as Types from '../types';

type Config = {
    siteId: string;
    childIds: Array<Types.Child['id']>;
    selectedPlanId?: Types.Plan['id'];
};

type Result = {
    planOverview: null | Types.PlanOverview;
    isLoading: boolean;
};

const usePlanOverview = ({siteId, childIds, selectedPlanId}: Config): Result => {
    const [planOverview, {loading}] = useTransformQuery<PlanOverviewQuery, PlanOverviewQueryVariables>()(
        PlanOverview,
        {
            skip: isEmpty(childIds),
            variables: {childIds, siteId, view: View.Edit, selectedPlanId},
        },
        ({data}) => {
            if (!hasValue(data?.finance.invoiceSummary.v1.overview.planOverviewV2)) {
                return null;
            }
            const planOverview = data.finance.invoiceSummary.v1.overview.planOverviewV2;

            return transformPlanOverview(planOverview);
        },
    );

    return {planOverview, isLoading: loading};
};

type PlanOverview = PlanOverviewQuery['finance']['invoiceSummary']['v1']['overview']['planOverviewV2'];

export const transformPlanOverview = (planOverview: PlanOverview): Types.PlanOverview => {
    return {
        plans: pipe(planOverview.plans, transformPlans),
    };
};

export const transformPlans = (plans: PlanOverview['plans']): Array<Types.OverviewPlanItem> => {
    return (
        plans
            .map<null | Types.OverviewPlanItem>(plan => {
                switch (plan.__typename) {
                    case 'InvoiceSummaryPlanV2':
                        return {
                            type: 'plan',
                            id: plan.id,
                            tuitionName: plan.tuitionName,
                            total: plan.total,
                            tag: getPlanTag(plan.timeframe, plan.billingState),
                            dateRange: {
                                fromInclusive: moment(plan.dateRange.from),
                                toInclusive: plan.dateRange.to ? moment(plan.dateRange.to) : null,
                            },
                            billingFrequency: transformBillingFrequency(plan.billingFrequency),
                            isSelected: plan.isSelected ?? false,
                        };
                    case 'InvoiceSummaryBreakV2':
                        return {
                            type: 'break',
                            id: plan.id,
                            tag: getPlanTag(plan.timeframe, plan.billingState),
                            dateRange: {
                                fromInclusive: moment(plan.dateRange.from),
                                toInclusive: plan.dateRange.to ? moment(plan.dateRange.to) : null,
                            },
                            isSelected: plan.isSelected ?? false,
                        };
                    default:
                        return null;
                }
            })
            // TODO: handle null state with sentry warning
            .filter(hasValue)
    );
};

export const transformBillingFrequency = (billingFrequency: BillingFrequency): Types.InitialBillingFrequency | null => {
    switch (billingFrequency) {
        case BillingFrequency.Monthly:
            return 'monthly';
        case BillingFrequency.Weekly:
        case BillingFrequency.Biweekly:
            return 'weekly';
        default:
            return null;
    }
};

export const getBillingStateCopy = (billingState: BillingState): string => {
    switch (billingState) {
        case BillingState.FullyBilled:
        case BillingState.PartiallyBilled:
            return needsTranslations('SimplifiedUsBilling', 'Already billed');
        case BillingState.Unbilled:
        default:
            return needsTranslations('SimplifiedUsBilling', 'Previous plan');
    }
};

export const getPlanTag = (timeframe: Timeframe, billingState: BillingState): null | Types.OverviewPlan['tag'] => {
    switch (timeframe) {
        case Timeframe.Current:
            return {
                type: 'in-effect',
                text: needsTranslations('SimplifiedUsBilling', 'In effect'),
            };
        case Timeframe.Future:
            return {
                type: 'future-plan',
                text: needsTranslations('SimplifiedUsBilling', 'Future plan'),
            };
        case Timeframe.Past:
            return {
                type: 'previous-plan',
                text: getBillingStateCopy(billingState),
            };
        default:
            return null;
    }
};

export {usePlanOverview};
